import { useEffect, useRef, useState } from 'react'
import { SmartLink } from './SmartLink'
import { LanguageSelector, useLanguage } from '../i18n'

export function Header() {
  const { copy } = useLanguage()
  const [isScrolled, setIsScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const navigationRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const updateHeader = () => setIsScrolled(window.scrollY > 24)
    updateHeader()
    window.addEventListener('scroll', updateHeader, { passive: true })
    return () => window.removeEventListener('scroll', updateHeader)
  }, [])

  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen)
    if (!menuOpen) return () => document.body.classList.remove('menu-open')

    const firstLink = navigationRef.current?.querySelector<HTMLElement>('a, button:not([disabled])')
    firstLink?.focus()

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
        return
      }

      if (event.key !== 'Tab' || !navigationRef.current) return
      const focusable = [...navigationRef.current.querySelectorAll<HTMLElement>('a, button:not([disabled])')]
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleKeydown)
    return () => {
      document.body.classList.remove('menu-open')
      window.removeEventListener('keydown', handleKeydown)
    }
  }, [menuOpen])

  return (
    <header className={`site-header ${isScrolled ? 'site-header--scrolled' : ''} ${menuOpen ? 'site-header--menu-open' : ''}`}>
      <div className="header-inner">
        <a href="#top" className="brand-link" aria-label="GeniusLEAD — voltar ao topo" onClick={() => setMenuOpen(false)}>
          <span className="header-brand-stack">
            <img className="header-logo" src="/assets/logos/geniuslead-header.svg" alt="" aria-hidden="true" />
            <span className="header-brand-meta">
              <span>GESTÃO · EXECUÇÃO · TECNOLOGIA · CRESCIMENTO</span>
              <em>Eficiência como princípio.</em>
            </span>
          </span>
        </a>

        <button
          ref={menuButtonRef}
          className="menu-toggle"
          type="button"
          aria-label={menuOpen ? copy.navigation.closeMenu : copy.navigation.openMenu}
          aria-expanded={menuOpen}
          aria-controls="primary-navigation"
          onClick={() => setMenuOpen((current) => !current)}
        >
          <span />
          <span />
        </button>

        <button
          className={`menu-backdrop ${menuOpen ? 'menu-backdrop--visible' : ''}`}
          type="button"
          aria-label={copy.navigation.closeMenu}
          tabIndex={menuOpen ? 0 : -1}
          onClick={() => setMenuOpen(false)}
        />

        <nav
          ref={navigationRef}
          id="primary-navigation"
          className={`primary-nav ${menuOpen ? 'primary-nav--open' : ''}`}
          aria-label={copy.navigation.ariaLabel}
        >
          <div className="primary-nav__links">
            {copy.navigation.links.map(([label, href]) => (
              <SmartLink
                key={label}
                href={href}
                onClick={() => setMenuOpen(false)}
              >
                {label}
              </SmartLink>
            ))}
          </div>
          <LanguageSelector />
          <a className="button button--small" href="mailto:comercial@geniuslead.com.br" onClick={() => setMenuOpen(false)}>
            {copy.navigation.contact}
          </a>
        </nav>
      </div>
    </header>
  )
}
